import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy } from "react";
import { Layout } from "./components/layout/Layout";
import { Home } from "./pages/Home";
import { Services } from "./pages/Services";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ContentProvider } from "./contexts/ContentContext";
import { WebsiteAccessibility } from "./pages/WebsiteAccessibility";
import { CMSPage } from "./pages/CMSPage";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsOfService } from "./pages/TermsOfService";
import { CitiesWeServe } from "./pages/CitiesWeServe";
import { CityServicePage } from "./pages/CityServicePage";
import { PromoPopup } from "./components/PromoPopup";
import { ScrollToTop } from "./components/ScrollToTop";

// Lazy load admin pages — visitors never download this code
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin").then(m => ({ default: m.AdminLogin })));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts").then(m => ({ default: m.AdminContacts })));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials").then(m => ({ default: m.AdminTestimonials })));
const AdminServices = lazy(() => import("./pages/admin/AdminServices").then(m => ({ default: m.AdminServices })));
const AdminContent = lazy(() => import("./pages/admin/AdminContent").then(m => ({ default: m.AdminContent })));
const AdminPages = lazy(() => import("./pages/admin/AdminPages").then(m => ({ default: m.AdminPages })));
const AdminPageEditor = lazy(() => import("./pages/admin/AdminPageEditor").then(m => ({ default: m.AdminPageEditor })));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia").then(m => ({ default: m.AdminMedia })));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers").then(m => ({ default: m.AdminUsers })));
const AdminActivity = lazy(() => import("./pages/admin/AdminActivity").then(m => ({ default: m.AdminActivity })));
const AdminNavigation = lazy(() => import("./pages/admin/AdminNavigation").then(m => ({ default: m.AdminNavigation })));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog").then(m => ({ default: m.AdminBlog })));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor").then(m => ({ default: m.AdminBlogEditor })));

const AdminSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100">
    <div className="spinner" />
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Admin routes wrapper
const AdminRoutes = () => {
  return (
    <Suspense fallback={<AdminSpinner />}>
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Routes>
                  <Route index element={<AdminDashboard />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="pages" element={<AdminPages />} />
                  <Route path="pages/:pageId" element={<AdminPageEditor />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="services" element={<AdminServices />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="activity" element={<AdminActivity />} />
                  <Route path="navigation" element={<AdminNavigation />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="blog/:postId" element={<AdminBlogEditor />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <AuthProvider>
        <ContentProvider>
          <BrowserRouter>
            <ScrollToTop />
            <PromoPopup />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/services" element={<Layout><Services /></Layout>} />
              <Route path="/services/residential" element={<Layout><CMSPage slug="residential" /></Layout>} />
              <Route path="/services/commercial" element={<Layout><CMSPage slug="commercial" /></Layout>} />
              <Route path="/services/repair-maintenance" element={<Layout><CMSPage slug="repair-maintenance" /></Layout>} />
              <Route path="/services/renovations" element={<Layout><CMSPage slug="renovations" /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/specials" element={<Layout><CMSPage slug="specials" /></Layout>} />
              <Route path="/website-accessibility" element={<Layout><WebsiteAccessibility /></Layout>} />
              <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />
              <Route path="/cities-we-serve" element={<Layout><CitiesWeServe /></Layout>} />
              <Route path="/pool-service/:citySlug" element={<Layout><CityServicePage /></Layout>} />
              <Route path="/p/:slug" element={<Layout><CMSPage /></Layout>} />
              
              {/* Admin routes */}
              <Route path="/admin/*" element={<AdminRoutes />} />
            </Routes>
          </BrowserRouter>
        </ContentProvider>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
